import { supabase } from './supabase'

// Thin seam over Supabase Auth MFA so login, the challenge screen, settings and
// the re-auth modal all go through one place. Spec: specs/002-mfa-account-security.

export type Aal = 'aal1' | 'aal2' | null

/** Current session assurance vs. the level the account can reach. */
export async function getAal(): Promise<{ current: Aal; next: Aal }> {
  const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
  return { current: (data?.currentLevel ?? null) as Aal, next: (data?.nextLevel ?? null) as Aal }
}

/**
 * True when the account HAS a verified factor but this session has not yet
 * satisfied it (aal1 while aal2 is reachable). Users without MFA return false,
 * so nothing changes for them.
 */
export async function mfaChallengeRequired(): Promise<boolean> {
  const { current, next } = await getAal()
  return current === 'aal1' && next === 'aal2'
}

export async function listTotpFactors() {
  const { data, error } = await supabase.auth.mfa.listFactors()
  if (error) throw error
  return data.totp ?? []
}

/** The verified TOTP factor id (used to drive the login/re-auth challenge), if any. */
export async function primaryFactorId(): Promise<string | null> {
  const factors = await listTotpFactors()
  const chosen = factors.find((f) => f.status === 'verified') ?? factors[0]
  return chosen?.id ?? null
}

/** Begin TOTP enrollment: returns the factor id, a scannable QR (SVG) and the manual secret. */
export async function enrollTotp(friendlyName?: string) {
  const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName })
  if (error) throw error
  return { factorId: data.id, qrSvg: data.totp.qr_code, secret: data.totp.secret, uri: data.totp.uri }
}

/** Confirm an enrollment OR complete a login/re-auth challenge with a 6-digit code. */
export async function verifyCode(factorId: string, code: string) {
  const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: code.trim() })
  if (error) throw error
}

/** Remove a factor. Supabase requires the session to already be aal2 to unenroll. */
export async function disableFactor(factorId: string) {
  const { error } = await supabase.auth.mfa.unenroll({ factorId })
  if (error) throw error
}
